// Показ приложенного изображения: чека, подтверждения перевода, скана заявления.
//
// Содержимое файла отдаётся ТОЛЬКО по токену, а тег изображения его не несёт: `<img src>` на
// адрес сервера получает отказ и рисует сломанную картинку (поймано живым прогоном 15.08.2026).
// Байты забираются общей дверью клиента и показываются локальной ссылкой на объект.
//
// Общий с 26.08.2026: жил в разделе «Деньги» как показ чека и понадобился второму разделу —
// скану заявления о реквизитах в карточке человека. Существительное («чек», «скан заявления»)
// стало параметром: без него вторая копия появилась бы ровно ради двух слов в тексте ошибки, а
// вместе с ней — второй разбор адреса и второй способ показать неудачу.

import { useEffect, useState } from 'react';

import { requestFile } from '@/shared/api/client';
import { ApiError } from '@/shared/errors/api-error';
import * as Alert from '@/shared/ui/alert';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { Skeleton } from '@/shared/ui/screen-state';

export interface AttachmentImageProps {
  /** Полный адрес файла с сервера либо путь. Пусто — адреса не пришло вовсе. */
  url: string | null;
  alt: string;
  /** Что именно показываем, в винительном падеже: «чек», «скан заявления». */
  что: string;
  /** Чем объяснить отсутствие адреса: у чека и у заявления причины разные. */
  нетАдреса?: string;
}

export function AttachmentImage({ url, alt, что, нетАдреса }: AttachmentImageProps) {
  const [ссылка, setСсылка] = useState<string | null>(null);
  const [сбой, setСбой] = useState<ApiError | null>(null);
  const [заход, setЗаход] = useState(0);

  useEffect(() => {
    if (url === null) {
      return;
    }
    let живо = true;
    let созданная: string | null = null;
    setСсылка(null);
    setСбой(null);
    void requestFile(путьФайла(url))
      .then(({ blob }) => {
        if (!живо) {
          return;
        }
        созданная = URL.createObjectURL(blob);
        setСсылка(созданная);
      })
      .catch((ошибка: unknown) => {
        if (живо) {
          setСбой(вОшибкуОбмена(ошибка, что));
        }
      });
    return () => {
      живо = false;
      if (созданная !== null) {
        URL.revokeObjectURL(созданная);
      }
    };
  }, [url, заход, что]);

  if (url === null) {
    // Бесконечный скелет здесь был бы худшим вариантом: «грузится» и «нечего грузить» человек
    // различить не может и ждёт минутами. Правило 7 — пустое и неизвестное выглядят по-разному.
    return (
      <Alert.Root variant='lighter' status='warning' size='small'>
        <Alert.Icon />
        {нетАдреса ??
          `Адрес файла не пришёл. Запись есть, открыть ${что} нечем — сообщите администратору.`}
      </Alert.Root>
    );
  }

  if (сбой !== null) {
    // Причина называется ТА, что вернул сервер, а не выдуманная. Раньше здесь на любой сбой
    // было написано «содержимого в хранилище нет», и по обрыву связи на секунду администратор
    // шёл искать в хранилище файл, который лежит на месте.
    return (
      <div className='flex flex-col items-start gap-2'>
        <ErrorAlert error={сбой} />
        <Button.Root
          type='button'
          variant='neutral'
          mode='stroke'
          size='xxsmall'
          onClick={() => setЗаход((прежний) => прежний + 1)}
        >
          Показать {что} ещё раз
        </Button.Root>
      </div>
    );
  }

  if (ссылка === null) {
    return <Skeleton className='h-60 w-full rounded-lg' />;
  }

  return (
    <img
      src={ссылка}
      alt={alt}
      // Байты приехали, а картинкой не оказались — так бывает у чека, приложенного в pdf.
      // Без перехвата браузер рисует свой значок поломки с подписью, и человек читает это как
      // «система сломана», хотя документ на месте. Тот же класс, что и у миниатюры в очереди
      // приёмки (03.09.2026), и лечится он тем же: пусть отвечает конверт ошибки, а не браузер.
      onError={() =>
        setСбой(
          new ApiError({
            code: 'attachment_not_an_image',
            message: `${что} не открывается как изображение.`,
            hint: 'Похоже, документ приложен не картинкой — например, в pdf.',
            next_action: 'Скачайте его целиком и откройте на компьютере.',
          }),
        )
      }
      className='max-h-96 w-full rounded-lg border border-stroke-soft-200 object-contain'
    />
  );
}

/**
 * Адрес файла приезжает от сервера ПОЛНЫМ, вместе с именем узла, а общий клиент ждёт путь и сам
 * подставляет адрес сервера. Без отрезания запрос уходит мимо и файл не открывается.
 */
export function путьФайла(адрес: string): string {
  try {
    const разобран = new URL(адрес, window.location.origin);
    return `${разобран.pathname}${разобран.search}`;
  } catch {
    return адрес;
  }
}

/** Всё, что не конверт сервера (сбой самого браузера), тоже обязано объяснить себя человеку. */
function вОшибкуОбмена(ошибка: unknown, что: string): ApiError {
  if (ApiError.is(ошибка)) {
    return ошибка;
  }
  return new ApiError({
    code: 'attachment_render_failed',
    message: `Не удалось показать ${что}.`,
    hint: ошибка instanceof Error ? ошибка.message : String(ошибка),
    next_action: 'Повторите; если не помогает, откройте запись заново.',
  });
}
